import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import axiosInstance from 'interceptor/axiosInstance';
import { ResponseTypes } from 'enums/responseTypes';

const MarketMappedServices = () => {
  const MarketMappedRecords = ({ filename, filters, sorting, status = null, provider = null }: any) => {

    const { data, isLoading, isError, isPreviousData, fetchNextPage, isFetching, refetch } = useInfiniteQuery({
      queryKey: ['market', filename, status, filters, sorting, provider],
      queryFn: async ({ pageParam = 0 }) => {
        const params = {
          offset: pageParam * 30,
          limit: 30,
          filters: JSON.stringify(filters ?? []),
          sorting: JSON.stringify(sorting ?? []),
          Filename: filename,
        };
        const modifiedURL = provider ?  `market/${provider.toLowerCase()}` : 'market';
        const response = await axiosInstance.get(`smart-mapping/${modifiedURL}/mapped`, { params });
        return response.data['result'];
      },
      getNextPageParam: (_lastGroup, groups) => groups.length,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });

    return {
      data,
      isLoading,
      isError,
      refetch,
      isPreviousData,
      isFetching
    }
  }

  const MarketMappedRecordsCount = ({ filename, filters, sorting, status = null, provider = null }: any) => {
    const { data, isError, isLoading, refetch } = useQuery({
      queryKey: ['marketMappedCount', filename, filters, sorting, provider],
      queryFn: async () => {
        const params = {
          filters: JSON.stringify(filters ?? []),
          sorting: JSON.stringify(sorting ?? []),
          Filename: filename
        };
        const modifiedURL = provider ? `market/${provider.toLowerCase()}` : 'market';
        const response = await axiosInstance.get(`smart-mapping/${modifiedURL}/mapped/count`, { params });
        return response.data['total_count'];
      }
    });
    return {
      data,
      isLoading,
      isError,
      refetch
    }
  }

  const MarketUnprocessedRecordsCount = ({ filename, filters, sorting, provider = null }: any) => {
    const { data, isError, isLoading, refetch } = useQuery({
      queryKey: ['marketUnprocessedCount', filename, filters, sorting, provider],
      queryFn: async () => {
        const params = {
          filters: JSON.stringify(filters ?? []),
          sorting: JSON.stringify(sorting ?? []),
          Filename: filename,
        };
        const modifiedURL = provider ? `market/${provider.toLowerCase()}` : 'market';
        const response = await axiosInstance.get(`smart-mapping/${modifiedURL}/unprocessed/count`, { params });
        return response.data['total_count'];
      }
    });
    return {
      data,
      isLoading,
      isError,
      refetch
    }
  }

  const downloadMappedMarketRecords = ({ filename, provider = null }: any) => {  
    const { isLoading: isDownloading, mutateAsync: triggerDownload } = useMutation({
      mutationFn: async () => {
        const modifiedURL = provider ? `market/${provider.toLowerCase()}` : 'market';
        const response = await axiosInstance.get(`smart-mapping/${modifiedURL}/mapped/download`, {
          params: { Filename: filename },
          responseType: ResponseTypes.BLOB
        });
        const contentDisposition =await response?.headers['content-disposition'];
        const matchName = contentDisposition.replace('attachment; filename=', '');
        const filenames = matchName.split('.')[0] + '.xlsx';

        const urlLink = window.URL.createObjectURL(new Blob([response?.data]));
        const link = document.createElement('a');
        link.href = urlLink;
        link.setAttribute('download', filenames);

        document.body.appendChild(link);
        link.click();

        return response
      }
    });
    return {
      isDownloading,
      triggerDownload
    };

  }

  const downloadUnprocessedMarketRecords = ({ filename, provider = null }: any) => {  
    const { isLoading: isDownloading, mutateAsync: triggerDownload } = useMutation({
      mutationFn: async () => {
        const modifiedURL = provider ? `market/${provider.toLowerCase()}` : 'market';
        const response = await axiosInstance.get(`smart-mapping/${modifiedURL}/unprocessed/download`, {
          params: { Filename: filename },
          responseType: ResponseTypes.BLOB
        });
        const contentDisposition =await response?.headers['content-disposition'];
        const matchName = contentDisposition.replace('attachment; filename=', '');
        const filenames = matchName.split('.')[0] + '.xlsx';

        const urlLink = window.URL.createObjectURL(new Blob([response?.data]));
        const link = document.createElement('a');
        link.href = urlLink;
        link.setAttribute('download', filenames);

        document.body.appendChild(link);
        link.click();
        return response
      }
    });
    return {
      isDownloading,
      triggerDownload
    };

  }

  const MarketUnprocessedRecords = ({ filename, filters, sorting, provider = null }: any) => {
    const { data, isLoading, error, isPreviousData, isFetching, fetchNextPage, refetch } = useInfiniteQuery({
      queryKey: ['market-unprocessed', filename, filters, sorting, provider],
      queryFn: async ({ pageParam = 0 }) => {
        const params = {
          offset: pageParam * 30,
          limit: 30,
          filters: JSON.stringify(filters ?? []),
          sorting: JSON.stringify(sorting ?? []),
          Filename: filename
        };
        const modifiedURL = provider ? `market/${provider.toLowerCase()}` : 'market';
        const response = await axiosInstance.get(`smart-mapping/${modifiedURL}/unprocessed`, { params });
        return response.data['result'];
      },
      getNextPageParam: (_lastGroup, groups) => groups.length,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });

    return {
      data,
      isLoading,
      error,
      refetch,
      isPreviousData,
      isFetching,
      fetchNextPage
    }
  }

  return {
    MarketMappedRecords,
    MarketUnprocessedRecords,
    downloadMappedMarketRecords,
    downloadUnprocessedMarketRecords,
    MarketMappedRecordsCount,
    MarketUnprocessedRecordsCount
  }
}

export default MarketMappedServices;
